(ns frontend.view.task.email.view
  (:require
   [basic-components.attachments :as attachments]
   [basic-components.collapsible-panel :as collapsible-panel]
   [basic-components.email-content :as email-content]
   [basic-components.step-indicator :as step-indicator]
   [clojure.edn :as edn]
   [clojure.string :as string]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.associations :as associations]
   [common.schema.entity-type-names :as entity-type-names]
   [common.schema.entity-types :as entity-types]
   [common.sync-progress :as sync-progress]
   [common.util :as cu]
   [frontend.components.drag-layout :as drag-layout]
   [frontend.page-components.default-page-layout :as default-page-layout]
   [frontend.page-components.zap-toolbar :as zap-toolbar]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [frontend.view.components.simple-editor :as simple-editor]
   [frontend.view.entity :as f-entity]
   [frontend.view.not-found :as not-found]
   [frontend.view.entity-viewers.scope :as scope]
   [frontend.view.syncing-data :as sync]
   [frontend.view.task.email.db :as db]
   [frontend.view.task.email.events :as events]
   [reagent.core :as r]
   [ui.components.breadcrumbs.view :as breadcrumbs]
   [ui.components.button.view :as button]
   [ui.components.toggle.view :as toggle]
   [ui.editors.data-input :as data-input]
   [ui.editors.open-search.view :as open-search]))

(defn resolve-selected-view
  [{:keys [state _task]}]
  (let [selected-view (-> @state
                          :selected-view)
        selected-view (or selected-view
                          :review-contacts
                          #_(case (:task/resolve-status task)
                              :task.email-status/contacts-reviewed :link-email
                              :review-contacts))]
    selected-view))

(defn toolbar-buttons
  [{:keys [state task]}]
  (let [selected-view (resolve-selected-view {:state state :task task})
        {:keys [task/completed?]} task
        no-email? @events/no-email?
        show-complete-button? (and (not completed?)
                                   (or (= selected-view :link-email)
                                       (:reviewed? @state)))
        show-reactivate-button? completed?]
    (concat
     (when show-reactivate-button?
       [{:toolbar-button?  true
         :dropdown-action? false
         :ord              15
         :primary-button?  false
         :on-select        (fn []
                             (events/reactivate-task! state task))
         :label            (loc/tr :reactivate-this-task)}])
     (when (or show-complete-button?
               no-email?)                                                                           ;;allow completing if email has been deleted
       [{:toolbar-button?  true
         :dropdown-action? false
         :ord              10
         :primary-button?  true
         :on-select        (fn []
                             (events/complete-task! task))
         :label            (loc/tr :mark-as-complete)}])
     [{:toolbar-button?  true
       :dropdown-action? false
       :ord              5
       :primary-button?  true
       :custom-style     "zap-btn__solid__salmon"
       :on-select        (fn []
                           (events/start-delete-task! task))
       :label            (loc/tr :delete-task)}
      {:toolbar-button?  true
       :dropdown-action? false
       :ord              6
       :primary-button   true
       :label            (loc/tr :reassign-task)
       :on-select        (fn []
                           (events/start-reassign-task! task))}])))

(defn breadcrumbs
  [{:keys [email]}]
  [breadcrumbs/container
   [breadcrumbs/item
    {:on-click (fn []
                 (routing/set-view! [:tasks-grid]))}
    (loc/tr :email-tasks)]
   [breadcrumbs/item
    {:active? true}
    (if @events/no-email?
      (loc/tr :email-not-found)
      (:email/subject email))]])

;;note: used only to force figwheel update:
(defonce tmp (r/atom {:c 0}))

(defonce drag-state (r/atom {}))

(defn attachments-section
  [{:keys [state attachments]}]
  [:div.email-task-email-section.mb-3
   ^{:key "attachments-panel"}
   [collapsible-panel/view
    {:state       state
     :start-open? true
     :panel-key   :attachments}
    (loc/tr :attachments-n [(count attachments)])
    [attachments/view attachments "email-task-attachments"]]])

(defn email-view
  []
  (let [email @events/email
        attachments @events/attachments
        {:keys [email/from email/to email/cc email/subject email/body]} email]
    [:div.email-task-email-container
     (when-not (empty? attachments)
       [attachments-section
        {:state       db/state
         :attachments attachments}])
     [:div.email-task-email-section
      [email-content/view
       {:from    from
        :to      to
        :cc      cc
        :subject subject
        :body    body}]]]))

(defn person-name
  [person]
  (str (string/trim
        (string/join " "
                     [(:person/first-name person)
                      (:person/last-name person)]))))

(defn association-label
  [association target-entity]
  (let [target-type (:entity/type target-entity)
        type (:entity-association/type association)
        label-text (cond (= target-type :entity.type/holding)
                         (loc/tr :holding-target-association [(loc/tr type)])
                         (some #{target-type}
                               [:entity.type/company
                                :entity.type/fundraising-opportunity
                                :entity.type/fund-commitment
                                :entity.type/oppo])
                         (loc/tr :person-association [(loc/tr type)])
                         :else (loc/tr type))]
    label-text))

(defn association-view
  [entity association]
  (let [target-entity (events/association-target entity association)
        icon (entity/default-icon target-entity)]
    [:div.related-entity.email-task-contact-association
     [:div.related-entity-inner
      [:span.related-entity__label
       [:span
        [:i
         {:class-name (fu/cs :fal
                             icon)}]
        [:span.associated-entity-name
         {:on-click (fn [_]
                      (if @events/completed?
                        (routing/open-entity target-entity)
                        (simple-editor/open-editor target-entity)))}
         (entity/resolve-entity-name target-entity session/get-entity session/reverse-lookup)]]]]]))

(defn type-associations-view
  [{:keys [person associations association-type]}]
  [:div.email-type-associations
   [:div.email-association-title
    (loc/tr :person-association [(loc/tr association-type)])]
   [:div.related-entities.email-task-associations
    (doall
     (for [association associations]
       ^{:key (str "association-" (:db/id association))}
       [association-view person association]))]])

(defn entity-associations-view
  [local-state person associations entity-type]
  (let [title (loc/tr :associated- [(cu/str-trim-lcase (entity-type-names/get-plural entity-type :en))])
        grouped (group-by :entity-association/type associations)]
    [collapsible-panel/view
     {:state       local-state
      :start-open? true
      :panel-key   entity-type}
     title
     [:div
      (doall
       (for [[type associations] grouped]
         ^{:key (str "type-associations-" type)}
         [type-associations-view
          {:local-state      local-state
           :entity-type      entity-type
           :association-type type
           :person           person
           :associations     associations}]))]]))

(defn association-entity-type
  [person association]
  (->> person
       :db/id
       (associations/other-entity-id association)
       (get @session/entities)
       :entity/type))

(defn associations-view
  [{:keys [local-state associations toggle-show!]} person]
  (let [grouped (group-by (partial association-entity-type person) associations)]
    [:div.email-contact-associations
     (doall
      (for [[entity-type entity-associations] grouped]
        ^{:key (str "entity-associations-" entity-type)}
        [entity-associations-view local-state person entity-associations entity-type]))
     [:div.d-flex
      [:span.email-contact-person__right
       [button/secondary-hollow
        {:class    "me-2"
         :on-click toggle-show!}
        (loc/tr :email-task-hide-associations)]]]]))

(defn contact-view
  [_ _]
  (let [local-state (r/atom {:show-associations? false})
        toggle-show-associations! (fn [_]
                                    (swap! local-state update :show-associations? not))]
    (fn [{:keys [state associations deletable?] :as args} person]
      (let [{:keys [person/universal-email-address person/avatar-url]} person
            avatar-url (fu/only-https-or-relative-img avatar-url)
            name (person-name person)
            initials (entity/get-initials person)
            has-associations? (seq associations)
            show-associations? (:show-associations? @local-state)
            show-delete? (and deletable?
                              (not @events/completed?))
            has-name? (not (string/blank? name))]
        [:div.email-contact-container
         [:div.email-contact-person
          [:div.email-contact-person__circle
           (if avatar-url
             [:img.professional-image-round.img-fluid
              {:src (fu/sanitize-img-url avatar-url)}]
             [:span.professional-image-initials
              (if-not (string/blank? initials)
                initials
                [:i.fal.fa-user])])]
          [:span.d-flex.flex-column
           [:span.btn-link
            {:on-click (fn [_]
                         (if @events/completed?
                           (routing/open-entity person)
                           (simple-editor/open-editor person)))}
            (if has-name?
              name
              universal-email-address)]
           (when has-name?
             [:span.email-contact-person__email universal-email-address])]
          (when (or (and has-associations?
                         (not show-associations?))
                    show-delete?)
            [:span.email-contact-person__right
             (when (and has-associations?
                        (not show-associations?))
               [button/secondary-hollow
                {:class-name "me-2"
                 :on-click   toggle-show-associations!}
                (loc/tr :email-task-show-associations)])
             (when show-delete?
               [fu/tooltip-overlay
                {:id        "delete-contact"
                 :placement "top"}
                (loc/tr :delete-contact-tooltip)
                [:span.email-contact-person__remove
                 [:i.fal.fa-trash
                  {:on-click (fn [_]
                               (events/disable-contact! state person))}]]])])]
         (when (and has-associations?
                    show-associations?)
           [associations-view
            (assoc args
              :local-state local-state
              :toggle-show! toggle-show-associations!) person])]))))

(defn existing-contacts-content
  [state task persons]
  [:div.existing-contacts
   (doall
    (for [contact persons]
      ^{:key (str "existing-" (:db/id contact))}
      [contact-view
       {:state        state
        :task         task
        :associations (events/entity-associations contact)}
       contact]))])

(defn existing-contacts-view
  [{:keys [state task persons no-panel?]}]
  (when-not (empty? persons)
    (if no-panel?
      [existing-contacts-content state task persons]
      ^{:key "existing-contacts-panel"}
      [collapsible-panel/view
       {:state       state
        :start-open? (< (count persons) 10)
        :panel-key   :existing-contacts}
       (loc/tr :existing-contacts-found-n [(count persons)])
       [existing-contacts-content state task persons]])))

(defn found-and-created-content
  [state task persons]
  [:div.existing-contacts
   (doall
    (for [contact persons
          :let [associations (events/entity-associations contact)
                delete-allowed? (not (contains? @events/prevent-delete (:db/id contact)))]]
      ^{:key (str "existing-" (:db/id contact))}
      [contact-view
       {:associations associations
        :state        state
        :task         task
        :deletable?   (and (empty? associations)
                           delete-allowed?)}
       contact]))])

(defn found-and-created-view
  [{:keys [state task persons no-panel?]}]
  (when-not (empty? persons)
    (if no-panel?
      [found-and-created-content state task persons]
      ^{:key "found-and-created-panel"}
      [collapsible-panel/view
       {:state       state
        :class-name  "mt-3"
        :start-open? (< (count persons) 10)
        :panel-key   :new-contacts-created}
       (loc/tr :new-contacts-found-created-n [(count persons)])
       [found-and-created-content state task persons]])))

(defn new-email-view
  [{:keys [email]}]
  [:div.new-email
   [:span.new-email__email
    {:title email}
    email]
   (when-not @events/completed?
     [:span.new-email__create-contact.clickable
      {:on-click (fn [_]
                   (f-entity/open-creator!
                    :entity.type/person
                    {:start-value email
                     :on-ok!      (fn [{:keys [new-entity-id close-cb!]}]
                                    (events/save-created-id! new-entity-id)
                                    (events/unhide-linkable! {:db/id new-entity-id})
                                    (events/toggle-related! {:db/id new-entity-id} true false false)
                                    (close-cb!))}))}
      [:i.fas.fa-plus-circle.mr-2]
      (loc/tr :new-contact)])])

(defn new-emails-content
  [state task emails]
  [:div.new-contacts
   (doall
    (for [email emails]
      ^{:key email}
      [new-email-view
       {:state state
        :task  task
        :email email}]))])

(defn new-emails-view
  [{:keys [state task emails no-panel?]}]
  (when-not (empty? emails)
    (if no-panel?
      [new-emails-content state task emails]
      ^{:key "new-emails-panel"}
      [collapsible-panel/view
       {:state       state
        :start-open? (< (count emails) 10)
        :panel-key   :new-emails}
       ;; when created, should show up in the above container
       (loc/tr :new-contacts-found-n [(count emails)])
       [new-emails-content state task emails]])))

(defn review-contacts-view
  [{:keys [state task]}]
  (let [resolved-contacts @events/resolve-contacts
        ;;uncomment following line to test 'No contacts found'
        ;;resolved-contacts [nil nil nil]
        [existing created emails-found] resolved-contacts
        all-empty? (every? empty? resolved-contacts)
        one-not-empty? (->> resolved-contacts
                            (remove empty?)
                            count
                            (= 1))
        one-contact? (and one-not-empty?
                          (->> resolved-contacts
                               (remove empty?)
                               first
                               count
                               (= 1)))]
    [:div.review-contacts
     [:div.review-contacts__title.text-center.mb-3
      (cond
        all-empty?
        (loc/tr :review-contacts-all-empty-title)
        one-contact?
        (loc/tr :review-contact-title)
        :else
        (loc/tr :review-contacts-title))]

     (when all-empty?
       [:div.text-center
        [:div.mb-4.mt-5
         [:img.img-fluid {:src "/img/undraw_empty.svg"}]]
        [:div
         [:strong (loc/tr :review-contacts-all-empty)]]])

     [existing-contacts-view
      {:state     state
       :task      task
       :persons   existing
       :no-panel? one-not-empty?}]

     [found-and-created-view
      {:state     state
       :task      task
       :persons   created
       :no-panel? one-not-empty?}]

     [new-emails-view
      {:state     state
       :task      task
       :emails    emails-found
       :no-panel? one-not-empty?}]]))

(defn linkable-entity-view
  [entity linked?]
  (let [entity-name (entity/resolve-entity-name entity session/get-entity session/reverse-lookup)
        completed? @events/completed?
        entity-type (:entity/type entity)
        icon (->> (get entity-types/entities entity-type)
                  :entity/icon)]
    [:div.linkable-entities-row.d-flex
     [:span.linkable-entities-row__type
      [:i {:class-name (fu/cs "fal" icon)}]]
     [:span.linkable-entities-row__name.clickable
      {:on-click (fn [_]
                   (if @events/completed?
                     (routing/open-entity entity)
                     (simple-editor/open-editor entity)))}
      entity-name]
     [:div.linkable-entities-row__status.d-flex.align-items-center
      [toggle/toggle
       {:checked?  (boolean linked?)
        :disabled? (boolean completed?)
        :on-change (fn [checked?]
                     (events/toggle-related! entity checked? (not checked?) false))}]
      (if linked?
        [:span.linkable-entities-row__status-text
         (loc/tr :linked)]
        [:span.linkable-entities-row__status-text
         (loc/tr :not-linked)])
      [:div.pl-2
       (when-not linked?
         [fu/tooltip-overlay
          {:id        "hide-from-linkable-entities-list-tooltip"
           :placement "top"}
          (loc/tr :hide-from-this-list)
          [:i.fas.fa-times-circle.clickable.text-danger
           {:on-click (fn [_]
                        (events/hide-linkable! entity))}]])]]]))

(defn link-email-view
  [{:keys [state]}]
  (let [all-entity-types (events/all-entity-types)
        selected-entity-type @events/selected-entity-type
        entity-types @events/entity-types
        linkable-entities @events/linkable-entities
        related-entities-set @events/related-entities-set
        linkable-contacts @events/linkable-contacts
        completed? @events/completed?]
    [:div.link-email
     (when-not (empty? linkable-contacts)
       [:div.mb-2
        [:strong (loc/tr :link-email-to-contacts-info)]])

     [:div.linkable-entities-header.d-flex
      [:span.linkable-entities-header__type (loc/tr :type)]
      [:span.linkable-entities-header__name (loc/tr :name)]
      [:span.linkable-entities-header__status (loc/tr :status)]]
     [:div.linkable-entities-list
      (for [entity linkable-contacts
            :let [entity-id (:db/id entity)
                  linked? (contains? related-entities-set entity-id)]]
        ^{:key (str "linkable-entity-" entity-id)}
        [linkable-entity-view entity linked?])]

     [:div.linkable-entities-list

      (for [entity linkable-entities
            :let [entity-id (:db/id entity)
                  linked? (contains? related-entities-set entity-id)]]
        ^{:key (str "linkable-entity-" entity-id)}
        [linkable-entity-view entity linked?])]

     [:div.mb-2
      [:strong.title-h5 (loc/tr :link-email-to-other-info)]]

     [:div.d-flex.justify-content-center.link-email-search-container
      [:div
       {:class (fu/cs "linkable-entity-search"
                      "full-size")}
       ^{:key (str "email-linked-entities-selector-" (pr-str entity-types))}
       [open-search/single-value
        {:value              nil
         :exclude-values     (map :db/id linkable-entities)
         :placeholder        (loc/tr :type-to-search)
         :field/entity-types entity-types
         :create!            (fn [input-str]
                               (f-entity/open-creator!
                                entity-types
                                {:start-value       input-str
                                 :skip-success-msg? true
                                 :on-ok!           (fn [{:keys [new-entity-id close-cb!]}]
                                                     (when (number? new-entity-id)
                                                       (events/add-linkable-entity! new-entity-id))
                                                     (when (fn? close-cb!)
                                                       (close-cb!)))}))
         :on-change          (fn [value _option]
                               (when (number? value)
                                 (events/add-linkable-entity! value)))}]]
      [:div
       {:class (fu/cs "entity-type-selector"
                      "full-size")}
       [data-input/view
        {:editor-type   :editor.type/select
         :disabled?     (boolean completed?)
         :is-clearable? false
         :options       (concat
                         [{:key   :all
                           :label (loc/tr :all-entity-types)}]
                         (->> all-entity-types
                              (map (fn [et]
                                     {:value (pr-str et)
                                      ;;note: no longer works
                                      :icon  (fn [_data]
                                               [:i.fal.fa-plus-circle.create-option__icon])
                                      :label (entity-type-names/get-plural et :en)}))
                              (sort-by :label)
                              (doall)))
         :value         (if (= :all selected-entity-type)
                          {:value :all
                           :label (loc/tr :all-entity-types)}
                          {:value (pr-str selected-entity-type)
                           :label (entity-type-names/get-plural selected-entity-type :en)})
         :on-change     (fn [value _option]
                          (events/select-entity-type! state (edn/read-string value)))}]]]]))

(defn review-contacts-step
  []
  [:span.d-flex.justify-content-center.align-items-center
   [:span.state-indicator__icon.mr-2 [:i.fal.fa-address-book]]
   [:div.state-indicator__text.d-flex.flex-column.justify-content-start.text-left
    [:div.step-text (loc/tr :step-n-n [1 2])]
    [:div (loc/tr :review-contacts)]]])

(defn link-email-step
  []
  [:span.d-flex.justify-content-center.align-items-center
   [:span.state-indicator__icon.mr-2 [:i.fal.fa-address-book]]
   [:div.state-indicator__text.d-flex.flex-column.justify-content-start.text-left
    [:div.step-text (loc/tr :step-n-n [2 2])]
    [:div (loc/tr :link-email)]]])


(defn action-view
  [{:keys [state task email]}]
  (let [selected-view (resolve-selected-view {:state state :task task})
        steps [{:key     :review-contacts
                :content [review-contacts-step]}
               {:key     :link-email
                :content [link-email-step]}]]
    [:div.email-task-action-container
     [:div.email-task-action-header.d-flex.justify-content-center.align-items-center
      [fu/tooltip-overlay
       {:id        "previous-step-tooltip"
        :placement "top"}
       (loc/tr :previous-step)
       [:div
        {:class-name (fu/cs "email-task-action__button clickable"
                            (when (= selected-view :review-contacts)
                              "visibility-hidden"))
         :on-click   (fn [_]
                       (swap! state assoc :selected-view :review-contacts))}
        [:i.fal.fa-arrow-circle-left]]]
      [:div.email-task-action__indicator
       [step-indicator/view
        {:current-step selected-view
         :task         task
         :steps        steps}]]
      [fu/tooltip-overlay
       {:id        "next-step-tooltip"
        :placement "top"}
       (loc/tr :next-step)
       [:div
        {:class-name (fu/cs "email-task-action__button clickable"
                            (when (= selected-view :link-email)
                              "visibility-hidden"))
         :on-click   (fn [_]
                       (swap! state (fn [s]
                                      (apply assoc
                                             s
                                             :selected-view
                                             :link-email
                                             (when-not (:reviewed? s)
                                               [:reviewed? true])))))}
        [:i.fal.fa-arrow-circle-right]]]]
     [:div.email-task-action-content
      (case selected-view
        :review-contacts
        [review-contacts-view
         {:state state
          :task  task
          :email email}]
        :link-email
        [link-email-view
         {:state state
          :task  task
          :email email}]
        [:div ""])

      ;;debug info - remove when done:
      #_[:div
         {:style {:border "dotted blue 1px"}}
         "debug: "
         [:span
          {:on-click (fn [_]
                       (if (= 17592186302086 task-id)
                         (routing/set-view! [:task-resolve/email :id "testing"])
                         (routing/set-view! [:task-resolve/email :id 17592186302086])))}
          " debug: click to test id change"]
         [:span.clickable
          {:style    {:border "dotted red 1px"}
           :on-click (fn [_]
                       (swap! state update :top-bottom? not))}
          "Click to toggle vertical/horizontal"]
         [:div (pr-str @state)]]]]))

(defn resolve-view
  [{:keys [task-id]}]
  (let [state db/state]
    (swap! state assoc :task-id task-id)
    (r/create-class
     {:component-will-unmount
      (fn [_]
        (reset! drag-state {})
        (reset! db/state {}))
      :reagent-render
      (fn [_]
        (let [task @events/task
              completed? @events/completed?
              no-email? @events/no-email?
              no-task? @events/no-task?
              ;;TODO fix top-bottom draggability when introducing footer-bar:
              ;;top-bottom? false
              #_(and (number? window-width)
                     (< window-width 834))
              top-bottom? (:top-bottom? @state)
              email @events/email
              selected-view (resolve-selected-view {:state state :task task})
              show-complete-button? (and (not completed?)
                                         (or (= selected-view :link-email)
                                             (:reviewed? @state)))]
          (if no-task?
            [not-found/not-found-content]
            [:<>
             (when-not (or (session/is-guest?)
                           no-task?)
               [zap-toolbar/view state
                (partial breadcrumbs
                         {:email      email
                          :tasks-grid (loc/tr :back-to/tasks-grid)})
                nil
                (toolbar-buttons {:state state
                                  :task  task})])
             (if no-email?
               [:div.entity__container
                [:div.entity-not-found
                 [:div.text-center
                  [:img.img-fluid
                   {:src "/img/Company-deleted.svg"}]]
                 [:p.text-center.py-4 (loc/tr :no-access-to-email)]]]
               [:div.entity__container
                [(if top-bottom?
                   drag-layout/top-bottom
                   drag-layout/left-right)
                 drag-state
                 "email-resolution-layout"
                 ;;data as reactions:
                 [email-view]
                 ;;data as params:
                 [action-view
                  {:state state
                   :task  task
                   :email email}]]])
             (when show-complete-button?
               [:div.footer-toolbar.hide-on-desktop
                (when (= selected-view :link-email)
                  [:div.footer-toolbar__left
                   [:div.footer-toolbar__buttons
                    [button/secondary-hollow
                     {:on-click (fn [_]
                                  (swap! state assoc :selected-view :review-contacts))}
                     (loc/tr :go-back)]]])
                [:div.footer-toolbar__right
                 [:div.footer-toolbar__buttons
                  [button/primary-solid
                   {:on-click #(events/complete-task! task)}
                   (loc/tr :mark-as-complete)]]]])])))})))

(defmethod routing/render-view :task-resolve/email
  [{:keys [route-params]}]
  (let [route-param-id (-> route-params
                           :id)
        task-id (if (= "testing" route-param-id)
                  "testing"
                  (-> route-params
                      :id
                      js/parseInt))]
    ;;the key change forces re-mounting of the component (and its child components)
    [default-page-layout/view
     ^{:key (str "email-task-resolve-id-" task-id)}
     [sync/wait-until-sync-progress sync-progress/COMPLETE
      ;; The page reads the task, its email and the attachments from the session.
      ;; A new-grid account does not get them from the sync. Hold a scope on the task.
      [scope/host
       {:entity-id    task-id
        :instance-id  :task-resolve
        :enabled?     (session/new-grid-2023?)
        :loading-view [sync/view]}
       [resolve-view
        {:task-id   task-id
         :tmp-count (:c @tmp)}]]]]))
